import {
  PlacedBlock,
  SocketConnection,
  TextAnnotation,
  LabelTemplate,
  LabelCell,
  BlockSystem,
  PartAttribute,
} from '@/app/types/cad';

// Cache für bereits gemeldete fehlende Attribute (um Spam zu vermeiden)
const reportedMissingAttributes = new Set<string>();

// Debug-Modus: Setzen Sie dies auf true, um detaillierte Warnungen zu sehen
const DEBUG_MODE = false; // Auf false setzen um Console-Spam zu vermeiden

/**
 * Aggressive Normalisierung eines Strings für Attribut-Suche
 * Entfernt alle unsichtbaren Zeichen, extra Whitespace, etc.
 */
function normalizeAttributeName(name: string): string {
  return name
    .trim()
    .normalize('NFC')
    .replace(/\s+/g, ' ')           // Mehrfache Spaces zu einem
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // Zero-width spaces
    .replace(/[\u00A0]/g, ' ');     // Non-breaking space zu normalem Space
}

/**
 * Berechnet die Levenshtein-Distanz zwischen zwei Strings für "Did you mean...?" Vorschläge
 */
function levenshteinDistance(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  const matrix: number[][] = [];

  for (let i = 0; i <= s2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= s1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= s2.length; i++) {
    for (let j = 1; j <= s1.length; j++) {
      if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  return matrix[s2.length][s1.length];
}

/**
 * Findet ähnliche Attributnamen für "Did you mean...?" Vorschläge
 */
function findSimilarAttributes(searchTerm: string, availableAttributes: string[]): string[] {
  const suggestions = availableAttributes
    .map(attr => ({
      attr,
      distance: levenshteinDistance(searchTerm, attr)
    }))
    .filter(item => item.distance <= 3) // Nur Attribute mit max. 3 Änderungen
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 3) // Top 3 Vorschläge
    .map(item => item.attr);

  return suggestions;
}

/**
 * Löst Attribute aus einem Element auf basierend auf dem targetElementType
 */
export function resolveElementAttributes(
  elementType: 'connection' | 'block' | 'text-annotation',
  elementId: string,
  blocks: PlacedBlock[],
  connections: SocketConnection[],
  textAnnotations: TextAnnotation[],
  systems: BlockSystem[]
): Map<string, string> {
  const attributeMap = new Map<string, string>();

  if (elementType === 'connection') {
    const connection = connections.find((c) => c.id === elementId);
    if (!connection) return attributeMap;

    // Connection-ID
    attributeMap.set('id', connection.id);
    attributeMap.set('connectionId', connection.id);

    // DN-Wert
    if (connection.dn !== undefined) {
      attributeMap.set('dn', `DN ${connection.dn}`);
      attributeMap.set('DN', `DN ${connection.dn}`); // Case-insensitive variant
    }

    // Diameter
    if (connection.diameter !== undefined) {
      attributeMap.set('diameter', `${connection.diameter} mm`);
      attributeMap.set('Diameter', `${connection.diameter} mm`); // Case-insensitive variant
    }

    // System
    if (connection.systemId) {
      const system = systems.find((s) => s.id === connection.systemId);
      if (system) {
        attributeMap.set('system', system.name);
        attributeMap.set('System', system.name); // Case-insensitive variant
        attributeMap.set('systemId', system.id);
        attributeMap.set('SystemId', system.id); // Case-insensitive variant
      }
    }

    // Position
    attributeMap.set('position', `U:${connection.position.u}, V:${connection.position.v}`);
    attributeMap.set('Position', `U:${connection.position.u}, V:${connection.position.v}`); // Case-insensitive variant
    attributeMap.set('u', connection.position.u.toString());
    attributeMap.set('U', connection.position.u.toString()); // Case-insensitive variant
    attributeMap.set('v', connection.position.v.toString());
    attributeMap.set('V', connection.position.v.toString()); // Case-insensitive variant

    // Anzahl der verbundenen Sockets
    if (connection.connectedSockets) {
      attributeMap.set('connectedCount', connection.connectedSockets.length.toString());
      attributeMap.set('ConnectedCount', connection.connectedSockets.length.toString()); // Case-insensitive variant
    }

    // Custom attributes - mit verschiedenen Case-Varianten
    if (connection.attributes) {
      connection.attributes.forEach((attr) => {
        const formattedValue = formatAttributeValue(attr);
        // Aggressive Normalisierung des Attribut-Namens
        const normalizedName = normalizeAttributeName(attr.name);
        
        // Speichere mit allen möglichen Varianten
        const variants = [
          normalizedName,                                           // Original
          normalizedName.toLowerCase(),                             // lowercase
          normalizedName.toUpperCase(),                             // UPPERCASE
          normalizedName.charAt(0).toUpperCase() + normalizedName.slice(1).toLowerCase(), // Capitalized
          normalizedName.charAt(0).toLowerCase() + normalizedName.slice(1),               // uncapitalized
        ];
        
        // Zusätzlich: NFD-Normalisierung (decomposed) für maximale Kompatibilität
        const nfdName = normalizeAttributeName(attr.name.normalize('NFD'));
        variants.push(
          nfdName,
          nfdName.toLowerCase(),
          nfdName.toUpperCase(),
          nfdName.charAt(0).toUpperCase() + nfdName.slice(1).toLowerCase()
        );
        
        // Alle Varianten speichern
        variants.forEach(variant => {
          attributeMap.set(variant, formattedValue);
        });
      });
    }
  } else if (elementType === 'block') {
    const block = blocks.find((b) => b.id === elementId);
    if (!block) return attributeMap;

    // Block-ID und Name
    attributeMap.set('id', block.id);
    attributeMap.set('blockId', block.id);
    attributeMap.set('name', block.blockData.name);
    attributeMap.set('blockName', block.blockData.name);

    // Version
    attributeMap.set('version', block.blockData.version);

    // System
    if (block.systemId) {
      const system = systems.find((s) => s.id === block.systemId);
      if (system) {
        attributeMap.set('system', system.name);
        attributeMap.set('System', system.name);
        attributeMap.set('systemId', system.id);
        attributeMap.set('SystemId', system.id);
      }
    }

    // Position
    attributeMap.set('position', `U:${block.transform.position.u}, V:${block.transform.position.v}`);
    attributeMap.set('Position', `U:${block.transform.position.u}, V:${block.transform.position.v}`);
    attributeMap.set('u', block.transform.position.u.toString());
    attributeMap.set('U', block.transform.position.u.toString());
    attributeMap.set('v', block.transform.position.v.toString());
    attributeMap.set('V', block.transform.position.v.toString());

    // Rotation
    attributeMap.set('rotation', `${block.transform.rotation}°`);
    attributeMap.set('Rotation', `${block.transform.rotation}°`);

    // Custom attributes - mit verschiedenen Case-Varianten
    if (block.blockData.attributes) {
      block.blockData.attributes.forEach((attr) => {
        const formattedValue = formatAttributeValue(attr);
        // Normalisiere den Attribut-Namen
        const normalizedName = attr.name.trim().normalize('NFC');
        // Original case
        attributeMap.set(normalizedName, formattedValue);
        // Lowercase
        attributeMap.set(normalizedName.toLowerCase(), formattedValue);
        // Uppercase first letter
        const capitalizedName = normalizedName.charAt(0).toUpperCase() + normalizedName.slice(1);
        attributeMap.set(capitalizedName, formattedValue);
        // All uppercase
        attributeMap.set(normalizedName.toUpperCase(), formattedValue);
      });
    }
  } else if (elementType === 'text-annotation') {
    const textAnnotation = textAnnotations.find((t) => t.id === elementId);
    if (!textAnnotation) return attributeMap;

    // Text-ID und Inhalt
    attributeMap.set('id', textAnnotation.id);
    attributeMap.set('text', textAnnotation.text);

    // Position
    attributeMap.set('position', `U:${textAnnotation.position.u}, V:${textAnnotation.position.v}`);
    attributeMap.set('u', textAnnotation.position.u.toString());
    attributeMap.set('v', textAnnotation.position.v.toString());

    // Font size
    attributeMap.set('fontSize', textAnnotation.fontSize.toString());

    // Color
    attributeMap.set('color', textAnnotation.color);
  }

  return attributeMap;
}

/**
 * Formatiert einen Attribut-Wert für die Anzeige
 */
function formatAttributeValue(attr: PartAttribute): string {
  if (attr.type === 'dn') {
    const value = typeof attr.value === 'number' ? attr.value : parseInt(attr.value as string, 10);
    return `DN ${value}`;
  } else if (attr.type === 'dn-reduction' && attr.dnValues) {
    return `DN ${attr.dnValues.dn1}/${attr.dnValues.dn2}`;
  } else if (attr.type === 'dimension' && attr.unit) {
    return `${attr.value} ${attr.unit}`;
  } else if (attr.unit) {
    return `${attr.value} ${attr.unit}`;
  } else if (typeof attr.value === 'boolean') {
    return attr.value ? 'Ja' : 'Nein';
  } else {
    return attr.value.toString();
  }
}

/**
 * Löst eine Label-Template auf und erzeugt die resolved Data basierend auf dem Target-Element
 */
export function resolveLabelData(
  template: LabelTemplate,
  elementType: 'connection' | 'block' | 'text-annotation',
  elementId: string,
  blocks: PlacedBlock[],
  connections: SocketConnection[],
  textAnnotations: TextAnnotation[],
  systems: BlockSystem[]
): string[][] {
  const attributeMap = resolveElementAttributes(
    elementType,
    elementId,
    blocks,
    connections,
    textAnnotations,
    systems
  );

  // Debug: Log alle verfügbaren Attribute
  const availableAttributes = Array.from(attributeMap.keys());
  
  if (DEBUG_MODE) {
    console.log('🔍 Label Resolver - Element:', elementType, elementId);
    console.log('🔍 Label Resolver - Available attributes:', availableAttributes.join(', '));
  }

  const resolvedData: string[][] = [];

  for (const row of template.rows) {
    const resolvedRow: string[] = [];
    for (const cell of row) {
      if (cell.type === 'text') {
        // Freitext
        resolvedRow.push(cell.content);
      } else if (cell.type === 'attribute') {
        // Attribut-Platzhalter auflösen mit aggressiver Normalisierung
        const normalizedSearchTerm = normalizeAttributeName(cell.content);
        const nfdSearchTerm = normalizeAttributeName(cell.content.normalize('NFD'));
        
        if (DEBUG_MODE) {
          console.log(`🔍 Searching for attribute: \"${cell.content}\" (NFC: \"${normalizedSearchTerm}\", NFD: \"${nfdSearchTerm}\")`);
          console.log(`   Length: ${cell.content.length} → NFC: ${normalizedSearchTerm.length}, NFD: ${nfdSearchTerm.length}`);
          console.log(`   Char codes:`, Array.from(cell.content).map(c => c.charCodeAt(0)));
        }
        
        // Versuche erst direkte Suche mit NFC
        let value = attributeMap.get(normalizedSearchTerm);
        let matchedKey = normalizedSearchTerm;
        
        // Wenn nicht gefunden, versuche mit NFD
        if (!value) {
          value = attributeMap.get(nfdSearchTerm);
          matchedKey = nfdSearchTerm;
        }
        
        // Wenn nicht gefunden, versuche case-insensitive Suche mit allen Normalisierungen
        if (!value) {
          const searchVariants = [
            normalizedSearchTerm.toLowerCase(),
            nfdSearchTerm.toLowerCase(),
          ];
          
          if (DEBUG_MODE) {
            console.log(`   Direct match failed. Trying case-insensitive variants:`, searchVariants);
          }
          
          for (const searchVariant of searchVariants) {
            for (const [key, val] of attributeMap.entries()) {
              const normalizedKey = normalizeAttributeName(key).toLowerCase();
              const nfdKey = normalizeAttributeName(key.normalize('NFD')).toLowerCase();
              
              if (normalizedKey === searchVariant || nfdKey === searchVariant) {
                value = val;
                matchedKey = key;
                if (DEBUG_MODE) {
                  console.log(`✅ Label Resolver - Resolved \"${cell.content}\" → \"${value}\" (case-insensitive match with \"${key}\")`);
                }
                break;
              }
            }
            if (value) break;
          }
        } else {
          if (DEBUG_MODE) {
            console.log(`✅ Label Resolver - Resolved \"${cell.content}\" → \"${value}\"`);
          }
        }
        
        // Als letzter Versuch: Suche mit Levenshtein-Distanz = 0 (exakte Übereinstimmung ignoriert Unicode-Varianten)
        if (!value) {
          const exactMatches = availableAttributes.filter(attr => 
            levenshteinDistance(normalizedSearchTerm, attr) === 0
          );
          
          if (exactMatches.length > 0) {
            value = attributeMap.get(exactMatches[0]);
            if (DEBUG_MODE && value) {
              console.log(`✅ Label Resolver - Resolved \"${cell.content}\" → \"${value}\" (fuzzy match with \"${exactMatches[0]}\")`);
            }
          }
        }
        
        // Fallback auf Platzhalter-Anzeige - nur warnen wenn NICHT gefunden
        if (!value) {
          // Nur im Debug-Modus detaillierte Warnungen ausgeben
          if (DEBUG_MODE) {
            const similarAttributes = findSimilarAttributes(cell.content, availableAttributes);
            const similarAttributesString = similarAttributes.length > 0 ? `Did you mean: ${similarAttributes.join(', ')}?` : '';
            const warningMessage = 
              `⚠️ Label Resolver - Attribute \"${cell.content}\" not found in element ${elementType} ${elementId}\n` +
              `   Available attributes: ${availableAttributes.length > 0 ? availableAttributes.join(', ') : 'NONE'}\n` +
              (similarAttributesString ? `   ${similarAttributesString}\n` : '') +
              `   💡 Tipp: Fügen Sie das Attribut \"${cell.content}\" zum ${elementType === 'block' ? 'Block' : elementType === 'connection' ? 'Connection' : 'Element'} hinzu oder verwenden Sie ein vorhandenes Attribut.`;
            
            // Warnung nur einmal ausgeben
            if (!reportedMissingAttributes.has(cell.content)) {
              console.warn(warningMessage);
              reportedMissingAttributes.add(cell.content);
            }
          }
          
          value = '—';
        }
        
        resolvedRow.push(value);
      }
    }
    resolvedData.push(resolvedRow);
  }

  return resolvedData;
}

/**
 * Findet das Target-Element unter einem Punkt (für Click-Detection)
 */
export function findTargetElementAtPoint(
  point: { x: number; y: number },
  blocks: PlacedBlock[],
  connections: SocketConnection[],
  textAnnotations: TextAnnotation[],
  viewState: any,
  gridSize: number
): { type: 'connection' | 'block' | 'text-annotation'; id: string } | null {
  // Diese Funktion würde die Hit-Detection implementieren
  // Für jetzt geben wir null zurück - wird in Canvas implementiert
  return null;
}